import React from "react";
import PageHeader from "@/components/PageHeader";

const principles = [
  { title: "Transparency", body: "Open, consistent communication with investors, partners and stakeholders." },
  { title: "Accountability", body: "Clear lines of responsibility from board oversight through to delivery." },
  { title: "Investor Alignment", body: "Structures and incentives designed around investor interests." },
  { title: "Risk Management", body: "Identifying, measuring and mitigating risk across every platform." },
  { title: "Long-Term Stewardship", body: "Decisions evaluated against multi-decade impact, not short-term cycles." },
  { title: "Sustainable Value Creation", body: "Performance measured by both economic outcomes and community benefit." },
];

const oversightAreas = [
  "Board oversight",
  "Investor reporting",
  "Risk management",
  "Capital allocation",
  "Stakeholder engagement",
  "Strategic execution",
];

const operatingModel = [
  { title: "Holding Company", body: "Evergreen Global Holdings Ltd holds the sponsor relationship, sets governance standards and oversees all platforms." },
  { title: "Platform Companies", body: "Each platform sits in a discrete vehicle with bespoke capital structure, board and reporting cadence aligned to its lifecycle." },
  { title: "Investor Reporting", body: "Standardised reporting on financial performance, milestone delivery, risk and impact — calibrated to institutional expectations." },
  { title: "Independent Oversight", body: "Independent advisors and committees support boards on audit, risk and ESG matters, ensuring objective challenge and assurance." },
];

export default function Governance() {
  const lastUpdated = "1 February 2026";

  return (
    <>
      <PageHeader
        testId="governance-header"
        eyebrow="Governance"
        title="Governance is the foundation, not an afterthought."
        intro="Disciplined oversight, structured reporting and clear accountability — the conditions under which long-term value is created."
        image="https://customer-assets.emergentagent.com/job_evergreen-holdings/artifacts/9r0gs6gk_IMG_0642.jpeg"
        overlayTone="strong"
      />

      {/* Philosophy */}
      <section data-testid="governance-philosophy" className="evg-section bg-white">
        <div className="evg-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-16 gap-y-12">
            <div className="lg:col-span-5" data-reveal>
              <div className="evg-overline mb-8">Governance Philosophy</div>
              <h2 className="evg-display-lg">The foundation upon which long-term value is created.</h2>
            </div>
            <div className="lg:col-span-7" data-reveal data-reveal-delay="200">
              <p className="evg-body">
                Governance is not an afterthought. It is the foundation upon which long-term value
                is created. Evergreen applies disciplined governance principles across every stage
                of platform development — from origination through to long-term stewardship.
              </p>
              <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-4">
                {oversightAreas.map((item, i) => (
                  <div key={item} className="flex items-baseline gap-5 py-2">
                    <span className="evg-index">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-[15px] text-[#0A1F44] font-light">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Principles — editorial rows */}
      <section data-testid="governance-principles" className="evg-section bg-[#F5F6F8]">
        <div className="evg-container">
          <div className="max-w-3xl pb-20" data-reveal>
            <div className="evg-overline mb-8">Governance Principles</div>
            <h2 className="evg-display-lg">Six principles that guide every decision.</h2>
          </div>
          <div>
            {principles.map((p, i) => (
              <div
                key={p.title}
                data-testid={`governance-principle-${i + 1}`}
                data-reveal
                data-reveal-delay={String((i % 3) * 100)}
                className="py-10 border-t border-[#0A1F44]/12 last:border-b last:border-[#0A1F44]/12 grid grid-cols-12 gap-x-8 gap-y-3 items-baseline"
              >
                <span className="col-span-12 md:col-span-1 evg-index">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="col-span-12 md:col-span-4 font-serif text-2xl md:text-3xl leading-tight">{p.title}</h3>
                <p className="col-span-12 md:col-span-7 text-[15px] text-[#0A1F44]/72 leading-[1.8] font-light">
                  {p.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Operating model */}
      <section data-testid="governance-model" className="evg-section bg-white">
        <div className="evg-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-16 gap-y-12">
            <div className="lg:col-span-4" data-reveal>
              <div className="evg-overline mb-8">Operating Model</div>
              <h2 className="evg-display-lg">How governance is applied.</h2>
            </div>
            <div className="lg:col-span-8">
              {operatingModel.map((item, i) => (
                <div
                  key={item.title}
                  data-reveal
                  data-reveal-delay={String((i % 2) * 100)}
                  className="py-10 border-t border-[#0A1F44]/12 last:border-b last:border-[#0A1F44]/12 grid grid-cols-12 gap-x-8 gap-y-3 items-baseline"
                >
                  <h3 className="col-span-12 md:col-span-4 font-serif text-2xl md:text-3xl leading-tight">{item.title}</h3>
                  <p className="col-span-12 md:col-span-8 text-[15px] text-[#0A1F44]/72 leading-[1.8] font-light">
                    {item.body}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-20 pt-10 border-t border-[#0A1F44]/12">
            <span className="evg-meta" data-testid="governance-last-updated">
              Last Updated: {lastUpdated}
            </span>
          </div>
        </div>
      </section>
    </>
  );
}
